import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { RootState } from '../../types/graph';
import { getOnOffboardingDebugInfo } from '../../onboarding/orchestration/debug';

const formatJson = (value: unknown) => JSON.stringify(value, null, 2);

const JsonBlock = ({ title, value }: { title: string; value: unknown }) => (
  <div className="bg-[var(--dbg-bg-secondary)] rounded border border-[var(--dbg-border)] p-3">
    <h4 className="text-sm font-medium text-[var(--dbg-text)] mb-1">{title}</h4>
    <pre className="text-xs text-[var(--dbg-text-secondary)] whitespace-pre-wrap break-all font-mono max-h-72 overflow-auto">
      {formatJson(value)}
    </pre>
  </div>
);

export const OnOffboarding = () => {
  const state = useSelector((root: RootState) => root);
  const storedFlow = useSelector(
    (root: RootState) => root.graph.onboardingFlow,
  );
  const currentStepIndex = useSelector(
    (root: RootState) => root.graph.currentStepIndex,
  );

  const debugInfo = useMemo(() => getOnOffboardingDebugInfo(state), [state]);

  return (
    <div className="bg-[var(--dbg-bg)] p-4 flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">
          On/offboarding flow
        </h3>
        <p className="text-sm text-[var(--dbg-text-secondary)] mt-0.5">
          Shows computed onboarding and offboarding steps with all state and
          transformer inputs that influence calculation.
        </p>
      </div>

      <JsonBlock
        title="Computed steps"
        value={{
          ...debugInfo.computed,
          currentStepIndex,
          currentStep: storedFlow[currentStepIndex],
          storedReduxFlow: storedFlow,
          isInSyncWithComputed:
            JSON.stringify(storedFlow) ===
            JSON.stringify(debugInfo.computed.finalFlow),
        }}
      />

      <JsonBlock title="OnboardingContext" value={debugInfo.context} />

      <div className="bg-[var(--dbg-bg-secondary)] rounded border border-[var(--dbg-border)] p-3">
        <h4 className="text-sm font-medium text-[var(--dbg-text)] mb-1">
          Transformer trace
        </h4>
        <div className="flex flex-col gap-3">
          {debugInfo.flowComputation.transformers.map((transformer) => (
            <div
              key={transformer.name}
              className="rounded border border-[var(--dbg-border)] bg-[var(--dbg-bg)] p-3"
            >
              <p className="text-sm text-[var(--dbg-text)] font-semibold mb-1">
                {transformer.name}{' '}
                {transformer.changed ? '(changed)' : '(no-op)'}
              </p>
              <pre className="text-xs text-[var(--dbg-text-secondary)] whitespace-pre-wrap break-all font-mono max-h-64 overflow-auto">
                {formatJson(transformer)}
              </pre>
            </div>
          ))}
        </div>
      </div>

      <JsonBlock title="Influencing state" value={debugInfo.influencingState} />
    </div>
  );
};
